import { useQuery, type QueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';

/** The feature flags of this user and organization (docs/api/platform-console.md: `GET /api/v1/flags`, evaluated for the signed-in user). */
// The backend's flags in its own order (docs/api/flags.json), then the ones the frontend has before the backend lists them (see FRONTEND_AHEAD in the drift test).
export const FLAG_NAMES = ['connect_live', 'estimator_onsite', 'industry_page_claims', 'crew_work_visits', 'quote_nurture', 'payments_ledger', 'quickbooks', 'invoice_reminders', 'timesheets', 'request_form', 'activation_emails', 'autopay', 'job_checklists', 'mobile_app', 'visit_reminders', 'connected_apps', 'account_deletion', 'feedback', 'public_api', 'template_leads', 'funnel_offer', 'change_orders', 'list_time_window'] as const;
export type FlagName = (typeof FLAG_NAMES)[number];

/** Puts flags the session answer already carried into the cache, so `GET /v1/flags` is not requested right after it. Nothing when the answer had none. */
export function seedFlags(queryClient: QueryClient | null | undefined, flags: Record<string, boolean> | undefined): void {
  if (flags) queryClient?.setQueryData(['flags'], flags);
}

/** `enabled` false: nothing is requested (the auth gate asks only while signing in). */
export function useFlags(enabled = true) {
  return useQuery({
    queryKey: ['flags'],
    queryFn: async (): Promise<Partial<Record<FlagName, boolean>>> => (await apiClient.get('/v1/flags')).data?.data?.flags ?? {},
    staleTime: 60_000,
    retry: 1,
    enabled,
  });
}

/**
 * One flag. Off while the answer is loading and when it cannot be read (a screen behind a flag is never shown on a guess); the server gates
 * the routes itself, so this only decides what is linked and shown.
 */
export function useFlag(name: FlagName): boolean {
  const { data } = useFlags();
  return data?.[name] === true;
}
